import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import TableLayout from "../../layouts/TableLayout/NewTableLayout";
import { COLUMN_DEFINITIONS } from "./table.config";
import CreateModal from "./CreateModal";
import { manifestUpdated } from "../../redux/reducers/manifests/manifestsSlice";
import {
  useDeleteManifestsMutation,
  useGetManifestsQuery,
  useLazyGetManifestQuery,
  useUpdateManifestMutation,
} from "../../redux/services/manifests/manifestsApi";
import { usePrefetch } from "../../redux/api";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/access-control";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { createRoot } from "react-dom/client";
import ManifestLayout from "../../layouts/ManifestLayout";
import { COLUMN_DEFINITIONS as PDF_COLUMN_DEFINITIONS } from "./Show/pdf-table.config";
import { EmployeesFilterList, StatusFilterList } from "../../components";
import { useFetchAll } from "../../hooks/useFetchAll";

const PdfAutoDownloadTrigger = ({ download, handleDownloadPdf, onComplete }) => {
  useEffect(() => {
    if (!download || typeof handleDownloadPdf !== "function") return;

    let cancelled = false;
    (async () => {
      try {
        await handleDownloadPdf(false);
      } finally {
        if (!cancelled) onComplete?.();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [download, handleDownloadPdf, onComplete]);

  return null;
};

const Manifests = () => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasAnyPermission } = usePermissions();
  const canManageManifests = hasAnyPermission([
    PERMISSIONS.MANIFESTS_CREATE,
    PERMISSIONS.MANIFESTS_EDIT,
    PERMISSIONS.MANIFESTS_DELETE,
  ]);

  // Define filter types similar to Payouts
  const filterTypes = useMemo(
    () =>
      [
        {
          id: "dateCreated",
          type: "date",
          label: "Date Created",
          defaultText: "All Time",
          defaultValue: {
            id: "allTime",
            value: null,
          },
        },
        {
          id: "status",
          type: "multiple",
          label: "Status",
          defaultText: "All",
          content: StatusFilterList,
        },
        {
          id: "employeeId",
          type: "multiple",
          label: "Employee",
          defaultText: "All Employees",
          content: EmployeesFilterList,
        },
      ],
    []
  );

  // Use useFetchAll hook
  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch },
    filters: { filters, setFilters },
    queryResult: { data, isLoading, isFetching },
  } = useFetchAll({
    queryHook: useGetManifestsQuery,
    defaultPageSize: 20,
  });

  const [updateManifest, { isLoading: manifestUpdating }] =
    useUpdateManifestMutation();
  const [deleteManifests, { isLoading: isDeleting }] =
    useDeleteManifestsMutation();

  const [triggerGetManifest] = useLazyGetManifestQuery();

  const [createModalOpen, setCreateModalOpen] = useState(false);

  function handleEdit(itemId) {
    navigate(`/manifests/${itemId}`);
  }

  async function handleDelete(items) {
    const res = await deleteManifests({ data: items.map((item) => item._id) });

    toast(res.data.message, {
      type: res.data.status,
      toastId: "delete-manifests",
    });

    return res.data;
  }

  async function handleUpdate(e, setLoading) {
    const updateData = {
      id: e.row.original._id,
      data: e.data,
    };

    const res = await updateManifest(updateData).unwrap();

    toast(res.message, {
      type: res.status,
      toast: "manifest-index-updated",
    });

    // if (res.status === "success") {
    //   dispatch(manifestUpdated(res.data));
    setTimeout(() => {
      setLoading(false);
    }, 500);
    // }
  }

  const cannotCreateOrEdit = !canManageManifests;

  const downloadManifestPdf = useCallback(async ({ manifest }) => {
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.top = "-9999px";
    document.body.appendChild(container);

    const root = createRoot(container);

    return new Promise((resolve, reject) => {
      let finished = false;

      const cleanup = () => {
        try {
          root.unmount();
        } catch {
          // ignore
        }
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      };

      const handleComplete = () => {
        if (finished) return;
        finished = true;
        setTimeout(() => {
          cleanup();
          resolve();
        }, 100);
      };

      try {
        root.render(
          <ManifestLayout
            columns={PDF_COLUMN_DEFINITIONS}
            manifest={manifest}
            download={true}
          >
            <PdfAutoDownloadTrigger download={true} onComplete={handleComplete} />
          </ManifestLayout>
        );
      } catch (e) {
        finished = true;
        cleanup();
        reject(e);
      }

      setTimeout(() => {
        if (finished) return;
        finished = true;
        cleanup();
        reject(new Error("PDF generation timeout"));
      }, 15000);
    });
  }, []);

  const handleDownloadPDF = useCallback(
    async (selectedItems) => {
      const items = Array.isArray(selectedItems)
        ? selectedItems
        : selectedItems
        ? [selectedItems]
        : [];

      if (!items.length) {
        toast.info("Select at least one manifest to download.", {
          toastId: "manifests-download-pdf-none-selected",
        });
        return;
      }

      for (const item of items) {
        try {
          const id = item?._id;
          const manifest = id ? await triggerGetManifest({ id }).unwrap() : item;
          await downloadManifestPdf({ manifest });
        } catch (error) {
          console.error("Download failed:", error);
          toast.error("Failed to generate PDF", {
            toastId: "manifests-download-pdf-failed",
          });
        }
      }
    },
    [downloadManifestPdf, triggerGetManifest]
  );

  const columnsWithPdf = useMemo(() => {
    return [
      ...COLUMN_DEFINITIONS,
      {
        id: "download",
        header: "PDF",
        accessorKey: "download",
        cell: ({ row }) => {
          return (
            <button
              type="button"
              className="flex justify-center w-full cursor-pointer hover:text-primary-600"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadPDF(row.original);
              }}
              title="Download PDF"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </button>
          );
        },
      },
    ];
  }, [handleDownloadPDF]);

  const manifestRows = useMemo(() => {
    if (Array.isArray(data?.manifests)) return data.manifests;
    if (Array.isArray(data?.docs)) return data.docs;
    if (Array.isArray(data?.data?.manifests)) return data.data.manifests;
    if (Array.isArray(data?.data?.docs)) return data.data.docs;
    return [];
  }, [data]);

  const paginationData = useMemo(() => {
    if (!data) return null;
    return data?.data || data;
  }, [data]);

  return (
    <div className="w-full h-full min-h-0">
      <TableLayout
        id="manifests"
        loading={isLoading || isFetching}
        columns={columnsWithPdf.length ? columnsWithPdf : undefined}
        data={manifestRows}
        pagination={paginationData}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        handleDownloadPDF={handleDownloadPDF}
        onUpdate={handleUpdate}
        mainAction={{ onClick: () => setCreateModalOpen(true) }}
        hideCheckbox={cannotCreateOrEdit}
        hideActions={cannotCreateOrEdit}
        hideMainAction={cannotCreateOrEdit}
        fullTextSearch={handleGlobalSearch}
        globalSearchFilters={["manifestId"]}
        filters={filterTypes}
        onFiltersChange={setFilters}
      />
      <CreateModal
        modalOpen={createModalOpen}
        setModalOpen={setCreateModalOpen}
      />
    </div>
  );
};

export default Manifests;
